import { type FieldType } from '../constants';
import {
  CALLING_COUNTRY_CODES,
  type ValidationError,
} from '@wix/form-validator';
import {
  type PublicDonationProps,
  type PublicAppointmentProps,
  type PublicImageChoiceProps,
  type PublicProductListProps,
  type PublicFixedPaymentProps,
  type PublicPaymentInputProps,
  type PublicSubmitButtonProps,
  type PublicRichTextProps,
  type PublicTimeInputProps,
  type PublicRadioGroupProps,
  type PublicCheckboxGroupProps,
  type PublicDropdownProps,
  type PublicTagsProps,
  type PublicCheckboxProps,
  type PublicSignatureProps,
  type PublicRatingInputProps,
  type PublicNumberInputProps,
  type PublicDateTimeInputProps,
  type PublicDateInputProps,
  type PublicDatePickerProps,
  type PublicFileUploadProps,
  type PublicMultilineAddressProps,
  type PublicPhoneInputProps,
  type PublicTextInputProps,
  type PublicTextAreaProps,
  type PublicFieldsProps,
} from '../types/public-props';

const allCallingCountryCodes = Object.keys(CALLING_COUNTRY_CODES);

// TODO: strongly type all params
const MinMaxLengthMapper = (validation: any) => ({
  maxLength: validation?.string?.maxLength,
  minLength: validation?.string?.minLength,
});

const MinMaxItemsMapper = (validation: any) => ({
  minItems: validation?.array?.minItems,
  maxItems: validation?.array?.maxItems,
});

const MinMaxValuesMapper = (validation: any) => {
  return {
    minValue:
      validation?.predefined?.paymentOptions?.products?.[0]?.dynamicPriceOptions
        .minPrice,
    maxValue:
      validation?.predefined?.paymentOptions?.products?.[0]?.dynamicPriceOptions
        .maxPrice,
  };
};

const AcceptedDatesMapper = (validation: any) => ({
  acceptedDates:
    validation?.string?.dateOptions?.minimum === '$now'
      ? 'future'
      : validation?.string?.dateOptions?.maximum === '$now'
      ? 'past'
      : 'all',
});

const CustomOptionMapper = (props: any) => {
  const { addOther, addOtherLabel, addOtherPlaceholder } = props;
  return {
    customOption: addOther
      ? {
          label: addOtherLabel,
          placeholder: addOtherPlaceholder,
        }
      : undefined,
  };
};

// Convert ValidationError to error message string
const ErrorMapper = (
  error: ValidationError | undefined,
): string | undefined => {
  if (!error) {
    return undefined;
  }
  return error.errorMessage || 'Validation error';
};

const BaseMapper = (props: any) => {
  const { hideLabel, error, showLabel, ...rest } = props;

  return {
    ...rest,
    showLabel: hideLabel === undefined ? true : !hideLabel,
    error: ErrorMapper(error),
  };
};

const TextFieldMapper = (props: any): PublicTextInputProps => ({
  ...props,
  ...MinMaxLengthMapper(props?.validation),
  ...BaseMapper(props),
});

const TextAreaMapper = (props: any): PublicTextAreaProps => ({
  ...props,
  ...MinMaxLengthMapper(props?.validation),
  ...BaseMapper(props),
});

const PhoneInputMapper = (props: any): PublicPhoneInputProps => {
  const { defaultCountry, defaultCountryCode, validation } = props;
  const allowedCountryCodes =
    validation?.string?.phoneOptions?.allowedCountryCodes;

  return {
    ...props,
    ...BaseMapper(props),
    defaultCountryCode: defaultCountry ?? defaultCountryCode,
    countryCodes:
      allowedCountryCodes && allowedCountryCodes.length > 0
        ? allowedCountryCodes
        : allCallingCountryCodes,
  };
};

const MultilineAddressMapper = (props: any): PublicMultilineAddressProps => {
  const { view, validation } = props;
  return {
    ...props,
    ...BaseMapper(props),
    showAddressLine2: view?.fieldSettings?.addressLine2?.show,
    addressLine2Required:
      validation?.predefined?.multilineAddressOptions?.fields?.addressLine2
        ?.required,
  };
};

const DateInputMapper = (props: any): PublicDateInputProps => {
  const { hidePlaceholder, validation } = props;
  return {
    ...props,
    ...BaseMapper(props),
    ...AcceptedDatesMapper(validation),
    showPlaceholder: hidePlaceholder === undefined ? true : !hidePlaceholder,
  };
};

const DatePickerMapper = (props: any): PublicDatePickerProps => {
  const { validation } = props;
  return {
    ...props,
    ...BaseMapper(props),
    ...AcceptedDatesMapper(validation),
  };
};

const DateTimeInputMapper = (props: any): PublicDateTimeInputProps => {
  const { hidePlaceholder, validation } = props;
  return {
    ...props,
    ...BaseMapper(props),
    ...AcceptedDatesMapper(validation),
    showPlaceholder: hidePlaceholder === undefined ? true : !hidePlaceholder,
  };
};

const FileUploadMapper = (props: any): PublicFileUploadProps => {
  const { valueSliderLimit, uploadFileFormats } = props;
  return {
    ...props,
    ...BaseMapper(props),
    maxFiles: valueSliderLimit,
    allowedFileFormats: uploadFileFormats,
  };
};

const NumberInputMapper = (props: any): PublicNumberInputProps =>
  BaseMapper(props);

const CheckboxMapper = (props: any): PublicCheckboxProps => {
  const { error, ...rest } = props;
  return {
    ...rest,
    error: ErrorMapper(error),
  };
};

const SignatureMapper = (props: any): PublicSignatureProps => {
  const { uploadEnabled } = props;
  return {
    ...props,
    ...BaseMapper(props),
    imageUploadEnabled: uploadEnabled,
  };
};

const RatingInputMapper = (props: any): PublicRatingInputProps =>
  BaseMapper(props);

const RadioGroupMapper = (props: any): PublicRadioGroupProps => {
  return {
    ...props,
    ...BaseMapper(props),
    ...CustomOptionMapper(props),
  };
};

const CheckboxGroupMapper = (props: any): PublicCheckboxGroupProps => {
  const { validation } = props;
  return {
    ...props,
    ...BaseMapper(props),
    ...CustomOptionMapper(props),
    ...MinMaxItemsMapper(validation),
  };
};

const DropdownMapper = (props: any): PublicDropdownProps => BaseMapper(props);

const TagsMapper = (props: any): PublicTagsProps => {
  const { validation } = props;
  return {
    ...props,
    ...BaseMapper(props),
    ...CustomOptionMapper(props),
    ...MinMaxItemsMapper(validation),
  };
};

const TimeInputMapper = (props: any): PublicTimeInputProps => {
  const { hidePlaceholder } = props;
  return {
    ...props,
    ...BaseMapper(props),
    showPlaceholder: hidePlaceholder === undefined ? true : !hidePlaceholder,
  };
};

const TextMapper = (props: any): PublicRichTextProps => props;

const SubmitButtonMapper = (props: any): PublicSubmitButtonProps => {
  return {
    ...props,
  };
};

const ProductListMapper = (props: any): PublicProductListProps => {
  return {
    ...props,
    ...BaseMapper(props),
    ...MinMaxItemsMapper(props?.validation),
  };
};

const FixedPaymentMapper = (props: any): PublicFixedPaymentProps => {
  const { validation, hideLabel, showLabel, ...rest } = props;
  const paymentValidation =
    validation?.predefined?.paymentOptions?.products?.[0];
  const fixedPaymentValue = paymentValidation?.fixedPriceOptions?.price;

  return {
    ...rest,
    validation,
    showLabel: hideLabel === undefined ? true : !hideLabel,
    amount: fixedPaymentValue,
  };
};

const PaymentInputMapper = (props: any): PublicPaymentInputProps => {
  const { validation } = props;
  return {
    ...props,
    ...BaseMapper(props),
    ...MinMaxValuesMapper(validation),
  };
};

const DonationMapper = (props: any): PublicDonationProps => {
  return {
    ...props,
    ...BaseMapper(props),
    ...CustomOptionMapper(props),
  };
};

const AppointmentMapper = (props: any): PublicAppointmentProps =>
  BaseMapper(props);

const ImageChoiceMapper = (props: any): PublicImageChoiceProps => {
  const { isMultipleAnswers, choiceImageResize } = props;
  return {
    ...props,
    ...BaseMapper(props),
    multiple: isMultipleAnswers,
    imageAspectRatio: choiceImageResize,
  };
};

const DefaultMapper = (props: any) => {
  const { error, ...rest } = props;
  return {
    ...rest,
    error: ErrorMapper(error),
  };
};

type PropsMapper = (props: any) => PublicFieldsProps;

export const FIELD_PROPS_MAPPERS: Record<FieldType, PropsMapper> = {
  TEXT_INPUT: TextFieldMapper,
  TEXT_AREA: TextAreaMapper,
  NUMBER_INPUT: NumberInputMapper,
  URL_INPUT: TextFieldMapper,
  DATE_INPUT: DateInputMapper,
  DATE_PICKER: DatePickerMapper,
  DATE_TIME_INPUT: DateTimeInputMapper,
  TIME_INPUT: TimeInputMapper,
  CONTACTS_COMPANY: TextFieldMapper,
  CONTACTS_POSITION: TextFieldMapper,
  CONTACTS_TAX_ID: TextFieldMapper,
  CONTACTS_FIRST_NAME: TextFieldMapper,
  CONTACTS_LAST_NAME: TextFieldMapper,
  CONTACTS_EMAIL: TextFieldMapper,
  CONTACTS_BIRTHDATE: DateInputMapper,
  CONTACTS_PHONE: PhoneInputMapper,
  CONTACTS_ADDRESS: TextFieldMapper,
  CONTACTS_SUBSCRIBE: CheckboxMapper,
  RADIO_GROUP: RadioGroupMapper,
  CHECKBOX: CheckboxMapper,
  CHECKBOX_GROUP: CheckboxGroupMapper,
  DROPDOWN: DropdownMapper,
  TAGS: TagsMapper,
  QUIZ_IMAGE_CHOICE: ImageChoiceMapper,
  QUIZ_MULTI_CHOICE: CheckboxGroupMapper,
  QUIZ_SINGLE_CHOICE: RadioGroupMapper,
  QUIZ_SHORT_TEXT: TextFieldMapper,
  QUIZ_LONG_TEXT: TextAreaMapper,
  QUIZ_NUMBER: NumberInputMapper,
  QUIZ_FILE_UPLOAD: FileUploadMapper,
  DEXT_TEXT_INPUT: TextFieldMapper,
  DEXT_TEXT_AREA: TextAreaMapper,
  DEXT_DROPDOWN: DropdownMapper,
  DEXT_URL_INPUT: TextFieldMapper,
  DEXT_RADIO_GROUP: RadioGroupMapper,
  DEXT_NUMBER_INPUT: NumberInputMapper,
  DEXT_CHECKBOX: CheckboxMapper,
  DEXT_CHECKBOX_GROUP: CheckboxGroupMapper,
  DEXT_EMAIL: TextFieldMapper,
  DEXT_PHONE: PhoneInputMapper,
  DEXT_RATING_INPUT: RatingInputMapper,
  DEXT_DATE_PICKER: DatePickerMapper,
  DEXT_TAGS: TagsMapper,
  MULTILINE_ADDRESS: MultilineAddressMapper,
  MLA_COUNTRY: DropdownMapper,
  MLA_CITY: TextFieldMapper,
  MLA_ADDRESS_LINE: TextFieldMapper,
  MLA_ADDRESS_LINE_2: TextFieldMapper,
  MLA_POSTAL_CODE: TextFieldMapper,
  MLA_SUBDIVISION: DropdownMapper,
  MLA_STREET_NAME: TextFieldMapper,
  MLA_STREET_NUMBER: TextFieldMapper,
  MLA_APARTMENT: TextFieldMapper,
  FULL_NAME: TextFieldMapper,
  FULL_NAME_FIRST_NAME: TextFieldMapper,
  FULL_NAME_LAST_NAME: TextFieldMapper,
  ECOM_ADDITIONAL_INFO: TextAreaMapper,
  ECOM_ADDRESS: TextFieldMapper,
  ECOM_FULL_NAME: TextFieldMapper,
  ECOM_PHONE: PhoneInputMapper,
  ECOM_COMPANY_NAME: TextFieldMapper,
  ECOM_EMAIL: TextFieldMapper,
  ECOM_SUBSCRIPTION: CheckboxMapper,
  ECOM_CONTACT_DETAILS: TextMapper,
  ECOM_SHIPPING_DETAILS: TextMapper,
  BOOKINGS_FIRST_NAME: TextFieldMapper,
  BOOKINGS_LAST_NAME: TextFieldMapper,
  BOOKINGS_EMAIL: TextFieldMapper,
  BOOKINGS_PHONE: PhoneInputMapper,
  BOOKINGS_ADDRESS: TextFieldMapper,
  BOOKINGS_HEADER: TextMapper,
  BOOKINGS_RICH_TEXT: TextMapper,
  BOOKINGS_LOGIN_BAR: DefaultMapper,
  APPOINTMENT: AppointmentMapper,
  SERVICES_DROPDOWN: DropdownMapper,
  SERVICES_MULTI_CHOICE: CheckboxGroupMapper,
  PRODUCT_LIST: ProductListMapper,
  FIXED_PAYMENT: FixedPaymentMapper,
  PAYMENT_INPUT: PaymentInputMapper,
  DONATION: DonationMapper,
  EVENTS_RSVP: RadioGroupMapper,
  EVENTS_REPEATER: TextMapper,
  FILE_UPLOAD: FileUploadMapper,
  SIGNATURE: SignatureMapper,
  RATING_INPUT: RatingInputMapper,
  VAT_ID: TextFieldMapper,
  NESTED_FORM: DefaultMapper,
  HEADER: TextMapper,
  RICH_TEXT: TextMapper,
  SUBMIT_BUTTON: SubmitButtonMapper,
  IDENTITY_PASSWORD: DefaultMapper,
};
